import { create } from 'zustand';
import { getUserProfile } from '@/services/data/users';

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  job_title: string | null;
}

interface UserState {
  // Profile data by userId
  profiles: Record<string, UserProfile | null>;
  // Loading states by userId
  loading: Record<string, boolean>;
  // Errors by userId
  errors: Record<string, Error | null>;
  
  // Actions
  fetchUserProfile: (userId: string) => Promise<void>;
  refreshUserProfile: (userId: string) => Promise<void>;
  getUserProfile: (userId: string) => UserProfile | null;
  getUserFullName: (userId: string, fallbackEmail?: string) => string;
  clearProfile: (userId: string) => void;
  clearAllProfiles: () => void;
}

export const useUserStore = create<UserState>((set, get) => ({
  // Initial state
  profiles: {},
  loading: {},
  errors: {},

  // Fetch user profile from API
  fetchUserProfile: async (userId: string) => {
    // Skip if already loading or already loaded
    const state = get();
    if (state.loading[userId] || state.profiles[userId] !== undefined) {
      return;
    }

    // Set loading state
    set((state) => ({
      loading: { ...state.loading, [userId]: true },
      errors: { ...state.errors, [userId]: null },
    }));

    try {
      const result = await getUserProfile(userId);
      
      if (result.success && result.data) {
        set((state) => ({
          profiles: { ...state.profiles, [userId]: result.data },
          loading: { ...state.loading, [userId]: false },
          errors: { ...state.errors, [userId]: null },
        }));
      } else {
        // Store null to indicate we tried but failed
        set((state) => ({
          profiles: { ...state.profiles, [userId]: null },
          loading: { ...state.loading, [userId]: false },
          errors: { ...state.errors, [userId]: result.error as Error || new Error('Failed to fetch profile') },
        }));
      }
    } catch (error) {
      set((state) => ({
        profiles: { ...state.profiles, [userId]: null },
        loading: { ...state.loading, [userId]: false },
        errors: { ...state.errors, [userId]: error as Error },
      }));
    }
  },

  // Force refresh user profile (bypasses cache)
  refreshUserProfile: async (userId: string) => {
    // Set loading state
    set((state) => ({
      loading: { ...state.loading, [userId]: true },
      errors: { ...state.errors, [userId]: null },
    }));

    try {
      const result = await getUserProfile(userId);
      
      if (result.success && result.data) {
        set((state) => ({
          profiles: { ...state.profiles, [userId]: result.data },
          loading: { ...state.loading, [userId]: false },
          errors: { ...state.errors, [userId]: null },
        }));
      } else {
        set((state) => ({
          profiles: { ...state.profiles, [userId]: null },
          loading: { ...state.loading, [userId]: false },
          errors: { ...state.errors, [userId]: result.error as Error || new Error('Failed to fetch profile') },
        }));
      }
    } catch (error) {
      set((state) => ({
        profiles: { ...state.profiles, [userId]: null },
        loading: { ...state.loading, [userId]: false },
        errors: { ...state.errors, [userId]: error as Error },
      }));
    }
  },

  // Get profile data (synchronous)
  getUserProfile: (userId: string) => {
    return get().profiles[userId] ?? null;
  },

  // Get formatted full name
  // Matches exact original logic:
  // `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || user.email?.split('@')[0] || ''
  getUserFullName: (userId: string, fallbackEmail?: string) => {
    const profile = get().profiles[userId];
    
    // If profile exists (success && data), use the same formatting logic
    if (profile) {
      const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      // If fullName is truthy, return it; otherwise fallback to email
      return fullName || fallbackEmail?.split('@')[0] || '';
    }
    
    // If no profile (failed fetch or not loaded), fallback to email
    return fallbackEmail?.split('@')[0] || '';
  },

  // Clear specific profile
  clearProfile: (userId: string) => {
    set((state) => {
      const { [userId]: _, ...profiles } = state.profiles;
      const { [userId]: __, ...loading } = state.loading;
      const { [userId]: ___, ...errors } = state.errors;
      return { profiles, loading, errors };
    });
  },

  // Clear all profiles
  clearAllProfiles: () => {
    set({ profiles: {}, loading: {}, errors: {} });
  },
}));

